import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardActionArea,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  useTheme,
  Fade,
  Paper,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Launch as LaunchIcon,
  Apps as AppsIcon,
  Tab as TabIcon,
  AccountCircle as AccountCircleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ArrowForward as ArrowForwardIcon,
  Dashboard as DashboardIcon,
  Whatshot as WhatshotIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Icon } from '@iconify/react';
import { useSelector, useDispatch } from 'react-redux';
import { agregaMenuActivo, estableceMenuActivo } from '../../redux/slices/menuSlice';
import { trackMenuSelection } from '../../helpers/analytics';
import { recordMenuVisit, getFrequentMenus } from '../../helpers/menuStorage';

const Home = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isDark = theme.palette.mode === 'dark';

  const { userData } = useSelector((state) => state.authReducer);
  const { menuList, menusAbiertos } = useSelector((state) => state.menuReducer);

  const [searchTerm, setSearchTerm] = useState('');
  const [visitVersion, setVisitVersion] = useState(0);
  const [selectedParentModule, setSelectedParentModule] = useState(null);

  function findByNombre(arr, nombre) {
    if (!arr || !Array.isArray(arr)) return null;
    return arr.find((item) => item.nombre === nombre) ?? null;
  }

  // Saludo dinámico según la hora del día
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return '¡Buenos días';
    if (hour >= 12 && hour < 20) return '¡Buenas tardes';
    return '¡Buenas noches';
  };

  // Formato de fecha en español
  const formattedDate = useMemo(() => {
    const date = new Date();
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    const dateStr = date.toLocaleDateString('es-CL', options);
    return dateStr.charAt(0).toUpperCase() + dateStr.slice(1);
  }, []);

  // Lista de módulos activos (excluyendo el Home idMenu: 0)
  const availableModules = useMemo(() => {
    if (!menuList || !Array.isArray(menuList)) return [];
    return menuList.filter((item) => item.idMenu !== 0 && item.visible !== false);
  }, [menuList]);

  // Cantidad de pestañas abiertas excluyendo el Home inicial (idMenu: 0)
  const openModulesCount = useMemo(() => {
    if (!menusAbiertos || !Array.isArray(menusAbiertos)) return 0;
    return menusAbiertos.filter((m) => m.idMenu !== 0 && m.idMenu !== '0').length;
  }, [menusAbiertos]);

  // Filtrado de módulos por término de búsqueda
  const filteredModules = useMemo(() => {
    let list = availableModules;

    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase().trim();
      list = list.filter((item) => {
        const matchesName = item.nombre?.toLowerCase().includes(term);
        const matchesSubmenu = item.menus?.some((sub) =>
          sub.nombre?.toLowerCase().includes(term)
        );
        return matchesName || matchesSubmenu;
      });
    }

    return list;
  }, [availableModules, searchTerm]);

  // Menús más visitados registrados en localStorage (validados contra backend)
  const frequentMenus = useMemo(() => {
    return getFrequentMenus(menuList, userData?.preferred_username, 4);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuList, userData?.preferred_username, visitVersion]);

  // Manejador para abrir un módulo
  const handleOpenMenu = (item) => {
    if (!item) return;

    // Si tiene submódulos, abrir el diálogo selector de submódulos
    if (item.menus && item.menus.length > 0) {
      setSelectedParentModule(item);
      return;
    }

    trackMenuSelection(item.nombre, item.idMenu);
    recordMenuVisit(item.idMenu, userData?.preferred_username);
    setVisitVersion((v) => v + 1);

    if (item.external) {
      const token = process.env.REACT_APP_LEVEGES_TOKEN || '';
      const username = userData?.preferred_username || '';
      const url = item.url.includes('?')
        ? `${item.url}&username=${username}&accesstoken=${token}`
        : `${item.url}?username=${username}&accesstoken=${token}`;
      window.open(url, item.target || '_blank');
    } else {
      dispatch(agregaMenuActivo(item));
      dispatch(estableceMenuActivo(item.idMenu));
    }

    // Cerrar modal de submódulos si estaba abierto
    if (selectedParentModule) {
      setSelectedParentModule(null);
    }
  };

  // Vista especial para corredores
  if (userData?.realm_access?.roles?.includes('acceso-corredores')) {
    const miLevel = findByNombre(menuList, 'Mi Level');
    return (
      <iframe
        title={miLevel?.nombre || 'Mi Level'}
        src={miLevel?.url}
        style={{
          width: '100%',
          minHeight: `calc(100vh - 90px)`,
          border: 'none',
        }}
        allowFullScreen
      />
    );
  }

  const userName = userData?.name || 'Usuario';
  const userEmail = userData?.email || '';

  const renderMenuIcon = (item, size = 24) => {
    if (!item) {
      return <Icon icon="solar:widget-3-outline" style={{ fontSize: size }} />;
    }
    const rawIcon = item.icono ? String(item.icono).replace(/[\r\n]+/g, '').trim() : '';

    if (!rawIcon) {
      return <Icon icon="solar:widget-3-outline" style={{ fontSize: size }} />;
    }

    if (rawIcon.startsWith('icon-') || item.iconify === false) {
      return <span className={rawIcon} style={{ fontSize: size }}></span>;
    }

    return <Icon icon={rawIcon} style={{ fontSize: size }} />;
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 3 },
        textAlign: 'left',
        height: '100%',
        maxHeight: '100%',
        overflowY: 'auto',
        boxSizing: 'border-box',
        bgcolor: isDark ? '#333333' : '#f7f7f7',
        color: isDark ? '#ffffff' : '#000000',
        fontFamily: 'Plus Jakarta Sans, sans-serif',
        '& *': {
          fontFamily: 'Plus Jakarta Sans, sans-serif',
        },
      }}
    >
      <Box sx={{ maxWidth: 1280, mx: 'auto', pb: 4 }}>
        {/* HERO BANNER */}
        <Fade in timeout={400}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2.5, sm: 3.5 },
              mb: 3,
              borderRadius: '12px',
              position: 'relative',
              overflow: 'hidden',
              background: isDark
                ? 'linear-gradient(135deg, #222222 0%, #2e2e2e 50%, #3d3d3d 100%)'
                : 'linear-gradient(135deg, #103B8F 0%, #1a4da6 55%, #ff9800 135%)',
              color: '#ffffff',
              boxShadow: isDark ? 3 : '0 8px 24px -4px rgba(16, 59, 143, 0.25)',
              border: isDark ? '1px solid #4f4f4f' : 'none',
            }}
          >
            {/* Elemento decorativo de fondo */}
            <Box
              sx={{
                position: 'absolute',
                top: -50,
                right: -50,
                width: 220,
                height: 220,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,152,0,0.25) 0%, rgba(255,255,255,0) 70%)',
                pointerEvents: 'none',
              }}
            />

            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    label="Level Desk"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      color: '#ffffff',
                      fontWeight: 600,
                      backdropFilter: 'blur(8px)',
                      fontSize: '0.75rem',
                      borderRadius: '8px',
                    }}
                  />
                  <Chip
                    icon={<CheckCircleOutlineIcon sx={{ '&&': { color: '#4ade80', fontSize: 16 } }} />}
                    label="Sistema en Línea"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.15)',
                      color: '#ffffff',
                      fontWeight: 500,
                      backdropFilter: 'blur(8px)',
                      fontSize: '0.75rem',
                      borderRadius: '8px',
                    }}
                  />
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', ml: 1 }}>
                    {formattedDate}
                  </Typography>
                </Stack>

                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.65rem', sm: '2rem', md: '2.25rem' },
                    lineHeight: 1.2,
                    mb: 1,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {getGreeting()},{' '}
                  <span style={{ color: '#fed7aa' }}>
                    {userName}
                  </span>
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: { xs: '0.88rem', sm: '0.95rem' },
                    maxWidth: 650,
                  }}
                >
                  Bienvenido a tu plataforma centralizada de gestión. Explora tus aplicaciones activas, accede rápidamente a tus herramientas de trabajo y gestiona tus tareas diarias.
                </Typography>
              </Grid>

              <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '10px',
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.12)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    minWidth: 240,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 50,
                      height: 50,
                      bgcolor: '#ff9800',
                      color: '#ffffff',
                      fontWeight: 700,
                      fontSize: '1.2rem',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                  >
                    {userName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ overflow: 'hidden' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ffffff', noWrap: true, fontSize: '0.95rem' }}>
                      {userName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', display: 'block', noWrap: true, mt: 0.25 }}>
                      {userEmail || userData?.preferred_username || 'Usuario'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Fade>

        {/* MÉTRICAS / TARJETAS RESUMEN */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={2}
              sx={{
                p: 2.25,
                borderRadius: '10px',
                bgcolor: isDark ? '#444444' : '#ffffff',
                border: isDark ? '1px solid #555555' : '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                },
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '10px',
                  bgcolor: isDark ? '#333333' : '#fff7ed',
                  color: '#ff9800',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AppsIcon fontSize="medium" />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: isDark ? '#bbbbbb' : '#64748b', fontWeight: 600 }}>
                  MÓDULOS ACTIVOS
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: isDark ? '#ffffff' : '#000000' }}>
                  {availableModules.length} Aplicaciones
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={2}
              sx={{
                p: 2.25,
                borderRadius: '10px',
                bgcolor: isDark ? '#444444' : '#ffffff',
                border: isDark ? '1px solid #555555' : '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                },
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '10px',
                  bgcolor: isDark ? '#333333' : '#eff6ff',
                  color: isDark ? '#90caf9' : '#103B8F',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <TabIcon fontSize="medium" />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: isDark ? '#bbbbbb' : '#64748b', fontWeight: 600 }}>
                  PESTAÑAS ABIERTAS
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: isDark ? '#ffffff' : '#000000' }}>
                  {openModulesCount} {openModulesCount === 1 ? 'Módulo activo' : 'Módulos activos'}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <Paper
              elevation={2}
              sx={{
                p: 2.25,
                borderRadius: '10px',
                bgcolor: isDark ? '#444444' : '#ffffff',
                border: isDark ? '1px solid #555555' : '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '10px',
                    bgcolor: isDark ? '#333333' : '#f0fdf4',
                    color: '#16a34a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AccountCircleIcon fontSize="medium" />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: isDark ? '#bbbbbb' : '#64748b', fontWeight: 600 }}>
                    USUARIO CONECTADO
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isDark ? '#ffffff' : '#000000' }}>
                    {userData?.preferred_username || userName}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* SECCIÓN ACCESOS FRECUENTES (DESDE LOCALSTORAGE) */}
        {frequentMenus.length > 0 && !searchTerm && (
          <Fade in timeout={400}>
            <Box sx={{ mb: 3.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
                <WhatshotIcon sx={{ color: '#ff9800' }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: isDark ? '#ffffff' : '#000000',
                    fontSize: '1.1rem',
                  }}
                >
                  Accesos Frecuentes
                </Typography>
                <Chip
                  label="Más visitados"
                  size="small"
                  sx={{
                    bgcolor: isDark ? '#222222' : '#fff7ed',
                    color: '#ff9800',
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    height: 22,
                    borderRadius: '6px',
                  }}
                />
              </Box>

              <Grid container spacing={2}>
                {frequentMenus.map((item, idx) => (
                  <Grid item xs={12} sm={6} md={3} key={`freq-${item.idMenu || idx}`}>
                    <Paper
                      elevation={2}
                      sx={{
                        borderRadius: '10px',
                        bgcolor: isDark ? '#444444' : '#ffffff',
                        border: isDark ? '1px solid #555555' : '1px solid #fed7aa',
                        transition: 'all 0.2s ease',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 4,
                          borderColor: '#ff9800',
                          '& .freq-icon': {
                            bgcolor: '#ff9800',
                            color: '#ffffff',
                          },
                          '& .freq-arrow': {
                            transform: 'translateX(3px)',
                            color: '#ff9800',
                          },
                        },
                      }}
                    >
                      <CardActionArea
                        onClick={() => handleOpenMenu(item)}
                        sx={{
                          p: 1.75,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          height: '100%',
                        }}
                      >
                        <Box
                          className="freq-icon"
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '8px',
                            bgcolor: isDark ? '#333333' : '#fff7ed',
                            color: '#ff9800',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            transition: 'all 0.2s ease',
                          }}
                        >
                          {renderMenuIcon(item, 22)}
                        </Box>
                        <Box sx={{ overflow: 'hidden', flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              color: isDark ? '#ffffff' : '#000000',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              fontSize: '0.88rem',
                            }}
                          >
                            {item.nombre}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark ? '#bbbbbb' : '#64748b',
                              fontSize: '0.72rem',
                              display: 'block',
                            }}
                          >
                            {item.visitCount} {item.visitCount === 1 ? 'visita' : 'visitas'}
                          </Typography>
                        </Box>
                        <ArrowForwardIcon
                          className="freq-arrow"
                          sx={{
                            fontSize: 16,
                            color: isDark ? '#888888' : '#94a3b8',
                            flexShrink: 0,
                            transition: 'all 0.2s ease',
                          }}
                        />
                      </CardActionArea>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Fade>
        )}

        {/* SECCIÓN DE MÓDULOS CON BUSCADOR */}
        <Box sx={{ mb: 3 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: isDark ? '#ffffff' : '#000000',
                  fontSize: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <DashboardIcon sx={{ color: '#ff9800' }} />
                Módulos y Aplicaciones
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? '#bbbbbb' : '#64748b', fontSize: '0.85rem' }}>
                Selecciona una herramienta para abrirla en una pestaña de trabajo.
              </Typography>
            </Box>

            <TextField
              size="small"
              placeholder="Buscar módulo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                width: { xs: '100%', sm: 280 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: isDark ? '#444444' : '#ffffff',
                  color: isDark ? '#ffffff' : '#000000',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: isDark ? '#bbbbbb' : '#64748b' }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon fontSize="small" sx={{ color: isDark ? '#bbbbbb' : '#64748b' }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Stack>

          {/* GRILLA DE TARJETAS DE MÓDULOS */}
          {filteredModules.length === 0 ? (
            <Paper
              elevation={2}
              sx={{
                p: 5,
                textAlign: 'center',
                borderRadius: '10px',
                bgcolor: isDark ? '#444444' : '#ffffff',
                border: isDark ? '1px solid #555555' : '1px solid #e2e8f0',
              }}
            >
              <AppsIcon sx={{ fontSize: 44, color: isDark ? '#777777' : '#cbd5e1', mb: 1.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: isDark ? '#ffffff' : '#334155' }}>
                No se encontraron módulos
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? '#bbbbbb' : '#64748b', mt: 0.5 }}>
                {searchTerm
                  ? `No hay herramientas que coincidan con "${searchTerm}".`
                  : 'No tienes módulos asignados actualmente.'}
              </Typography>
              {searchTerm && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSearchTerm('')}
                  sx={{ mt: 2, borderRadius: '8px', textTransform: 'none', color: '#ff9800', borderColor: '#ff9800' }}
                >
                  Limpiar búsqueda
                </Button>
              )}
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {filteredModules.map((item, index) => {
                const hasSubmenus = item.menus && item.menus.length > 0;

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item.idMenu || index}>
                    <Fade in timeout={250 + index * 30}>
                      <Card
                        elevation={2}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: '10px',
                          bgcolor: isDark ? '#444444' : '#ffffff',
                          border: isDark ? '1px solid #555555' : '1px solid #e2e8f0',
                          transition: 'all 0.2s ease',
                          position: 'relative',
                          overflow: 'hidden',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            borderColor: '#ff9800',
                            boxShadow: 4,
                            '& .module-icon-box': {
                              bgcolor: '#ff9800',
                              color: '#ffffff',
                            },
                            '& .module-arrow': {
                              transform: 'translateX(3px)',
                              color: '#ff9800',
                            },
                          },
                        }}
                      >
                        <CardActionArea
                          onClick={() => handleOpenMenu(item)}
                          sx={{
                            p: 2.25,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box sx={{ width: '100%' }}>
                            {/* Cabecera de la tarjeta: Icono y Badges */}
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%',
                                mb: 1.75,
                              }}
                            >
                              <Box
                                className="module-icon-box"
                                sx={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: '10px',
                                  bgcolor: isDark ? '#333333' : '#fff7ed',
                                  color: '#ff9800',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                {renderMenuIcon(item, 24)}
                              </Box>

                              <Stack direction="row" spacing={0.5}>
                                {item.external && (
                                  <Tooltip title="Abre enlace externo">
                                    <Chip
                                      icon={<LaunchIcon sx={{ '&&': { fontSize: 12 } }} />}
                                      label="Externo"
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.65rem',
                                        borderRadius: '6px',
                                        bgcolor: isDark ? '#222222' : '#f1f5f9',
                                        color: isDark ? '#bbbbbb' : '#64748b',
                                      }}
                                    />
                                  </Tooltip>
                                )}
                                {hasSubmenus && (
                                  <Chip
                                    label={`${item.menus.length} submódulos`}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.65rem',
                                      borderRadius: '6px',
                                      bgcolor: isDark ? '#222222' : '#eff6ff',
                                      color: isDark ? '#90caf9' : '#103B8F',
                                      fontWeight: 600,
                                    }}
                                  />
                                )}
                              </Stack>
                            </Box>

                            {/* Nombre del Módulo */}
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                fontSize: '1rem',
                                color: isDark ? '#ffffff' : '#000000',
                                mb: 0.5,
                              }}
                            >
                              {item.nombre}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                color: isDark ? '#bbbbbb' : '#64748b',
                                fontSize: '0.8rem',
                                minHeight: 34,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {hasSubmenus
                                ? `Acceso a ${item.menus.map((m) => m.nombre).slice(0, 2).join(', ')}${item.menus.length > 2 ? '...' : ''}`
                                : `Módulo de gestión y operación ${item.nombre}.`}
                            </Typography>
                          </Box>

                          {/* Pie de la tarjeta */}
                          <Box
                            sx={{
                              width: '100%',
                              mt: 1.75,
                              pt: 1.25,
                              borderTop: isDark ? '1px solid #555555' : '1px solid #f1f5f9',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                color: '#ff9800',
                                fontSize: '0.78rem',
                              }}
                            >
                              {hasSubmenus ? `Ver ${item.menus.length} submódulos` : 'Abrir aplicación'}
                            </Typography>
                            <ArrowForwardIcon
                              className="module-arrow"
                              sx={{
                                fontSize: 16,
                                color: isDark ? '#888888' : '#94a3b8',
                                transition: 'transform 0.2s ease, color 0.2s ease',
                              }}
                            />
                          </Box>
                        </CardActionArea>
                      </Card>
                    </Fade>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </Box>

      {/* MODAL / DIÁLOGO DE SUBMÓDULOS */}
      <Dialog
        open={Boolean(selectedParentModule)}
        onClose={() => setSelectedParentModule(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            p: 1,
            bgcolor: isDark ? '#333333' : '#ffffff',
            backgroundImage: 'none',
            border: isDark ? '1px solid #555555' : '1px solid #e2e8f0',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '8px',
                bgcolor: isDark ? '#222222' : '#fff7ed',
                color: '#ff9800',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {renderMenuIcon(selectedParentModule, 22)}
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: isDark ? '#ffffff' : '#000000', fontSize: '1.1rem' }}>
                {selectedParentModule?.nombre}
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? '#bbbbbb' : '#64748b' }}>
                Selecciona una opción para abrir:
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setSelectedParentModule(null)} size="small">
            <CloseIcon sx={{ color: isDark ? '#ffffff' : '#000000' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: isDark ? '#555555' : '#f1f5f9', p: 2 }}>
          <Grid container spacing={1.5}>
            {selectedParentModule?.menus?.map((subItem, idx) => (
              <Grid item xs={12} key={subItem.idMenu || idx}>
                <Paper
                  elevation={2}
                  sx={{
                    borderRadius: '10px',
                    bgcolor: isDark ? '#444444' : '#f7f7f7',
                    border: isDark ? '1px solid #555555' : '1px solid #e2e8f0',
                    transition: 'all 0.2s ease',
                    overflow: 'hidden',
                    '&:hover': {
                      bgcolor: isDark ? '#555555' : '#fff7ed',
                      borderColor: '#ff9800',
                      transform: 'translateX(3px)',
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => handleOpenMenu(subItem)}
                    sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '8px',
                          bgcolor: isDark ? '#333333' : '#ffffff',
                          color: '#ff9800',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: 1,
                        }}
                      >
                        {renderMenuIcon(subItem, 20)}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? '#ffffff' : '#000000', fontSize: '0.9rem' }}>
                          {subItem.nombre}
                        </Typography>
                        {subItem.external && (
                          <Chip
                            label="Externo"
                            size="small"
                            sx={{ height: 18, fontSize: '0.62rem', mt: 0.25, borderRadius: '4px' }}
                          />
                        )}
                      </Box>
                    </Box>
                    <ArrowForwardIcon sx={{ fontSize: 18, color: '#ff9800' }} />
                  </CardActionArea>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Home;



